<template>
  <Teleport to="body">
    <Transition name="panel-slide">
      <div
        v-if="panelState !== 'hidden'"
        class="floating-info-panel"
        :class="[`panel-${panelState}`, `panel-${position}`, { 'panel-pinned': isPinned }]"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
        @click="handlePanelClick"
      >
        <div class="panel-header">
          <div class="panel-title">
            <IconInformationOutline class="title-icon" />
            <span>Info de Producción</span>
          </div>
          <div class="panel-actions">
            <button
              v-if="!isMinimized"
              class="action-btn"
              :class="{ active: isPinned }"
              :title="isPinned ? 'Desfijar' : 'Fijar'"
              @click.stop="$emit('toggle-pin')"
            >
              <IconPin v-if="!isPinned" /><IconPinOff v-else />
            </button>
            <button
              class="action-btn"
              :title="isMinimized ? 'Ampliar' : 'Minimizar'"
              @click.stop="$emit('toggle-minimize')"
            >
              <IconWindowMinimize v-if="!isMinimized" /><IconWindowMaximize v-else />
            </button>
            <button
              v-if="isPinned"
              class="action-btn close-btn"
              title="Cerrar"
              @click.stop="$emit('hide-now')"
            >
              <IconClose />
            </button>
          </div>
        </div>

        <div
          v-if="!isMinimized && cantidades.length > 0"
          class="panel-selector"
        >
          <label class="selector-label">Cantidad:</label>
          <select
            class="custom-select"
            :value="cantidadSeleccionada"
            @change="$emit('cantidad-change', parseInt($event.target.value))"
          >
            <option
              v-for="c in cantidades"
              :key="c[cantidadItemKey]"
              :value="c[cantidadItemKey]"
            >
              {{ c.cantidad }} {{ c.unidadVenta || 'unidades' }}
            </option>
          </select>
        </div>

        <div
          v-if="!isMinimized"
          class="panel-tabs"
        >
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'producto' }"
            @click.stop="$emit('tab-change', 'producto')"
          >
            Producto
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'procesos' }"
            @click.stop="$emit('tab-change', 'procesos')"
          >
            Procesos
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'materiales' }"
            @click.stop="$emit('tab-change', 'materiales')"
          >
            Materiales
          </button>
        </div>

        <div
          v-if="!isMinimized"
          class="panel-body"
        >
          <div
            v-if="loading"
            class="panel-loading"
          >
            <div class="spinner"></div>
            <span>Cargando cálculos...</span>
          </div>

          <template v-else>
            <div
              v-if="activeTab === 'producto'"
              class="tab-content live-data"
            >
              <div class="data-group">
                <div class="group-title">Medidas del Producto</div>
                <div class="info-row">
                  <span>Ancho (Eje):</span> <strong>{{ datosProductoLive.anchoEje }} mm</strong>
                </div>
                <div class="info-row">
                  <span>Largo (Desarrollo):</span>
                  <strong>{{ datosProductoLive.largoDesarrollo }} mm</strong>
                </div>
                <div class="info-row">
                  <span>Desarrollo (Imp.):</span>
                  <strong>{{ datosProductoLive.desarrolloImposicion }} mm</strong>
                </div>
              </div>

              <div class="data-group">
                <div class="group-title">Imposición</div>
                <div class="info-row">
                  <span>Rep. Ancho (Cavidades):</span>
                  <strong>{{ datosProductoLive.repAncho }}</strong>
                </div>
                <div class="info-row">
                  <span>Rep. Alto (Cavidades):</span>
                  <strong>{{ datosProductoLive.repAlto }}</strong>
                </div>
                <div class="info-row">
                  <span>Total de bocas:</span> <strong>{{ datosProductoLive.totalBocas }}</strong>
                </div>
              </div>

              <div class="data-group">
                <div class="group-title">Material y Mermas</div>
                <div class="info-row">
                  <span>Laterales:</span> <strong>{{ datosProductoLive.laterales }} mm</strong>
                </div>
                <div class="info-row">
                  <span>Ancho mínimo:</span>
                  <strong>{{ datosProductoLive.anchoMinimo?.toFixed(2) }} mm</strong>
                </div>
                <div class="info-row">
                  <span>Ancho Material:</span>
                  <strong>{{ datosProductoLive.anchoMaterial }} mm</strong>
                </div>
                <div class="info-row">
                  <span>m² de la imposición:</span>
                  <strong>{{
                    formatNumberLocale(datosProducto.m2PorPliego, 4) ||
                    formatNumberLocale(datosProductoLive.m2, 4) ||
                    0
                  }}</strong>
                </div>
              </div>

              <div class="stale-alert info-alert">
                Estos datos reflejan tu configuración actual en tiempo real.
              </div>
            </div>

            <div
              v-else-if="activeTab === 'procesos'"
              class="tab-content relative-container"
            >
              <div
                v-if="isStale"
                class="stale-overlay"
              >
                <IconAlertOutline class="stale-icon-large" />
                <span>Modificaste las medidas del producto.</span>
                <span class="stale-sub">Cotiza nuevamente para actualizar los cálculos.</span>
              </div>

              <div :class="{ 'blur-content': isStale }">
                <div
                  v-for="(p, index) in procesosAgrupados"
                  :key="index"
                  class="data-card"
                >
                  <div class="card-title">
                    {{ p.titulo }}
                    <span
                      v-if="p.esFueraDeLinea"
                      class="card-badge-fuera-linea"
                      title="Este proceso no está encadenado en línea con el anterior en el mismo recurso"
                      >Fuera de línea</span
                    >
                  </div>
                  <div class="card-grid">
                    <div class="grid-item">
                      <span>Netos:</span> {{ formatearUnidad(p.metrosLinealesNetos, 'm', 2) }}
                    </div>
                    <div class="grid-item">
                      <span>Buenos:</span>
                      {{
                        formatearUnidad(
                          p.metrosLinealesBuenos,
                          p.unidadTrabajo?.abreviaturaUnidadCantidad,
                          2,
                        )
                      }}
                    </div>
                    <div
                      v-if="!p.esGrupo"
                      class="grid-item"
                    >
                      <span>Merma Fija:</span>
                      {{ formatearUnidad(p.mermaFija, resolverAbreviaturaMermaFija(p), 2) }}
                    </div>
                    <div
                      v-if="p.mermaColor > 0"
                      class="grid-item"
                    >
                      <span>Merma Color:</span>
                      {{ formatearUnidad(p.mermaColor, resolverAbreviaturaUnidadRecurso(p), 2) }}
                    </div>
                    <div
                      v-if="p.mermaPolimero > 0"
                      class="grid-item"
                    >
                      <span>{{ p.labelsMatrizImpresion.merma }}:</span>
                      {{ formatearUnidad(p.mermaPolimero, resolverAbreviaturaUnidadRecurso(p), 2) }}
                    </div>
                    <div
                      v-if="!p.esGrupo"
                      class="grid-item"
                    >
                      <span>Merma %:</span>
                      {{ formatNumberLocale(p.mermaPorcentual || 0, 2) }}%
                      <span
                        v-if="p.mermaPorcentualAbsoluta > 0"
                        style="color: #909399; font-size: 0.9em; margin-left: 4px"
                      >
                        ({{ formatNumberLocale(p.mermaPorcentualAbsoluta, 2) }}
                        {{ resolverAbreviaturaUnidadRecurso(p) }})
                      </span>
                    </div>
                    <div class="grid-item">
                      <span>Totales:</span>
                      {{
                        formatearUnidad(
                          p.metrosTotales,
                          p.unidadTrabajo?.abreviaturaUnidadCantidad,
                          2,
                        )
                      }}
                    </div>
                    <div class="grid-item">
                      <span>Hs Prod.:</span> {{ formatNumberLocale(p.horasProduccion, 2) }}
                    </div>
                    <div class="grid-item">
                      <span>Hs. Setup + recarga:</span> {{ p.horasSetupTotal?.toFixed(2) }}
                    </div>

                    <div
                      v-if="p.horasSetupDetalle?.recarga > 0"
                      class="grid-item"
                    >
                      <span>Hs Recarga:</span>
                      {{ formatNumberLocale(p.horasSetupDetalle.recarga, 2) }}
                    </div>

                    <div class="grid-item">
                      <span>Total Horas:</span>
                      <span style="color: #409eff; font-weight: bold">
                        {{ (p.horasTotalesLinea || 0).toFixed(2) }}
                      </span>
                    </div>

                    <div class="grid-item">
                      <span>Velocidad:</span>
                      {{
                        formatearUnidad(
                          p.velocidadLinea ?? p.velocidad,
                          unidadVelocidad(p.unidadTrabajo),
                          0,
                        )
                      }}
                    </div>
                    <div
                      v-if="p.divideBocas"
                      class="grid-item"
                    >
                      <span>Div. Bocas:</span> Sí
                    </div>
                  </div>

                  <div
                    v-if="p.esGrupo && p.procesos && p.procesos.length > 0"
                    class="merma-desglose"
                  >
                    <div class="merma-desglose-titulo">Merma por proceso:</div>
                    <div
                      v-for="proc in p.procesos"
                      :key="proc.codigoCotizacionElementoProceso"
                      class="merma-desglose-item"
                    >
                      <span class="merma-desglose-nombre">{{ proc.descripcion }}</span>
                      <span>
                        Fija: {{ (proc.mermaFija || 0).toFixed(2) }}
                        {{ resolverAbreviaturaMermaFija(proc) }}
                      </span>
                      <span>
                        {{ (proc.mermaPorcentual || 0).toFixed(2) }}%
                        <span v-if="proc.mermaPorcentualAbsoluta > 0">
                          ({{ proc.mermaPorcentualAbsoluta.toFixed(2) }}
                          {{ resolverAbreviaturaUnidadRecurso(proc) }})
                        </span>
                      </span>
                      <span v-if="proc.mermaColor > 0">
                        Color: {{ proc.mermaColor.toFixed(2) }}
                        {{ resolverAbreviaturaUnidadRecurso(proc) }}
                      </span>
                      <span v-if="proc.mermaPolimero > 0">
                        {{ p.labelsMatrizImpresion.merma }}: {{ proc.mermaPolimero.toFixed(2) }}
                        {{ resolverAbreviaturaUnidadRecurso(proc) }}
                      </span>
                      <span>
                        Total: {{ (proc.mermaTotal || 0).toFixed(2) }}
                        {{ resolverAbreviaturaUnidadRecurso(proc) }}
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  v-if="procesosAgrupados.length === 0"
                  class="empty-state"
                >
                  No hay procesos calculados.
                </div>
              </div>
            </div>

            <div
              v-else
              class="tab-content relative-container"
            >
              <div
                v-if="isStale"
                class="stale-overlay"
              >
                <IconAlertOutline class="stale-icon-large" />
                <span>Modificaste las medidas del producto.</span>
                <span class="stale-sub">Cotiza nuevamente para actualizar los cálculos.</span>
              </div>

              <div :class="{ 'blur-content': isStale }">
                <div
                  v-for="m in materialesAplanados"
                  :key="m.codigoMaterial"
                  class="data-card"
                >
                  <div class="card-title">{{ m.descripcionInterna }}</div>
                  <div
                    v-if="m.codigoEquivalente"
                    class="card-subtitle"
                  >
                    Cód. Eq: {{ m.codigoEquivalente }}
                  </div>
                  <div class="card-grid">
                    <div class="grid-item">
                      <span>Cantidad:</span> {{ formatNumberLocale(m.cantidad, 2) }}
                      {{ m.unidadMedida }}
                    </div>
                    <div class="grid-item">
                      <span>Merma:</span> {{ formatNumberLocale(m.cantidadMerma, 2) }}
                      {{ m.unidadMedida }}
                    </div>
                    <div
                      v-if="m.cantidadMermaMinima > 0"
                      class="grid-item"
                    >
                      <span>Merma mínima:</span> {{ m.cantidadMermaMinima?.toFixed(2) }}
                      {{ m.unidadMedida }}
                    </div>
                    <div
                      v-if="m.gramaje"
                      class="grid-item"
                    >
                      <span>Gramaje:</span> {{ m.gramaje }}
                    </div>
                    <div
                      v-if="m.densidad"
                      class="grid-item"
                    >
                      <span>Densidad:</span> {{ m.densidad }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="materialesAplanados.length === 0"
                  class="empty-state"
                >
                  No hay materiales calculados.
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </Transition>

    <Transition name="fade">
      <button
        v-if="panelState === 'hidden' && showToggleButton"
        class="floating-toggle-btn"
        :class="`toggle-${position}`"
        @click="$emit('show')"
      >
        <IconChartBoxOutline />
      </button>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue';
import IconInformationOutline from '~icons/mdi/information-outline';
import IconPin from '~icons/mdi/pin-outline';
import IconPinOff from '~icons/mdi/pin-off-outline';
import IconWindowMinimize from '~icons/mdi/window-minimize';
import IconWindowMaximize from '~icons/mdi/window-maximize';
import IconClose from '~icons/mdi/close';
import IconChartBoxOutline from '~icons/mdi/chart-box-outline';
import IconAlertOutline from '~icons/mdi/alert-outline';
import { formatNumberLocale } from '@/shared/utils/locale_number_utils.js';
import { agruparProcesosPorLinea } from '@/shared/utils/agrupar_procesos_produccion.js';
import {
  formatearUnidad,
  unidadVelocidad,
} from '@/shared/utils/format_produccion.js';
import {
  resolverAbreviaturaMermaFija,
  resolverAbreviaturaUnidadRecurso,
} from '@/shared/utils/unidad_trabajo.js';

const props = defineProps({
  panelState: { type: String, default: 'hidden' },
  isPinned: { type: Boolean, default: false },
  isMinimized: { type: Boolean, default: false },
  activeTab: { type: String, default: 'producto' },
  datosProductoLive: { type: Object, default: () => ({}) },
  datosProducto: { type: Object, default: () => ({}) },
  procesos: { type: Array, default: () => [] },
  materiales: { type: Array, default: () => [] },
  cantidades: { type: Array, default: () => [] },
  cantidadSeleccionada: { type: [Number, String], default: null },
  cantidadItemKey: { type: String, default: 'codigoCotizacionCantidad' },
  isStale: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  position: { type: String, default: 'bottom-right' },
  showToggleButton: { type: Boolean, default: true },
});

const emit = defineEmits([
  'show',
  'hide',
  'hide-now',
  'toggle-pin',
  'toggle-minimize',
  'cantidad-change',
  'tab-change',
]);

function handleMouseEnter() {
  emit('show');
}
function handleMouseLeave() {
  if (!props.isPinned && !props.isMinimized) emit('hide');
}
function handlePanelClick() {
  if (props.isMinimized) emit('toggle-minimize');
}

const procesosAgrupados = computed(() => agruparProcesosPorLinea(props.procesos));

const materialesAplanados = computed(() => {
  if (props.materiales && props.materiales.length > 0) return props.materiales;

  const mapa = new Map();

  props.procesos.forEach((p) => {
    if (p.materiales && Array.isArray(p.materiales)) {
      p.materiales.forEach((m) => {
        if (mapa.has(m.codigoMaterial)) {
          const existente = mapa.get(m.codigoMaterial);
          existente.cantidad += m.cantidad || 0;
        } else {
          mapa.set(m.codigoMaterial, { ...m });
        }
      });
    }
  });

  return Array.from(mapa.values());
});
</script>

<style scoped>
.floating-info-panel {
  position: fixed;
  z-index: 2000;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  border: 1px solid #e4e7ed;
  width: 340px;
  max-height: 520px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
}
.panel-bottom-right {
  bottom: 44px;
  right: 32px;
}
.panel-minimized {
  width: 48px;
  max-height: 48px;
  border-radius: 50%;
  cursor: pointer;
}
.panel-pinned {
  border-color: #409eff;
  box-shadow: 0 8px 32px rgba(64, 158, 255, 0.2);
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
}
.panel-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #303133;
}
.panel-actions {
  display: flex;
  gap: 4px;
}
.action-btn {
  background: transparent;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #909399;
}
.action-btn:hover {
  color: #409eff;
}
.panel-selector {
  padding: 8px 12px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  align-items: center;
  gap: 8px;
}
.selector-label {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
}
.custom-select {
  flex: 1;
  padding: 4px;
  font-size: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}
.panel-tabs {
  display: flex;
  border-bottom: 1px solid #e4e7ed;
  background: #fafafa;
}
.tab-btn {
  flex: 1;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  color: #909399;
  border-bottom: 2px solid transparent;
}
.tab-btn.active {
  color: #409eff;
  border-bottom-color: #409eff;
  background: #fff;
}
.panel-body {
  overflow-y: auto;
  flex: 1;
  padding: 12px;
  max-height: 380px;
  position: relative;
}
.info-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px dashed #ebeef5;
  font-size: 12px;
}
.info-row span:first-child {
  color: #606266;
}
.info-alert {
  margin-top: 10px;
  font-size: 11px;
  color: #409eff;
  background: #ecf5ff;
  padding: 8px;
  border-radius: 4px;
  text-align: center;
}

.data-card {
  background: #f8f9fa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 10px;
}
.card-title {
  font-weight: 700;
  font-size: 12px;
  color: #303133;
  margin-bottom: 6px;
  border-bottom: 1px solid #e4e7ed;
  padding-bottom: 4px;
}
.card-subtitle {
  font-size: 10px;
  color: #909399;
  margin-bottom: 6px;
}
.card-badge-fuera-linea {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #fdf6ec;
  color: #e6a23c;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  vertical-align: middle;
}
.card-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
}
.grid-item {
  font-size: 11px;
  color: #303133;
}
.grid-item span {
  color: #909399;
  font-weight: 600;
}

.merma-desglose {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #e4e7ed;
  font-size: 11px;
}

.merma-desglose-titulo {
  color: #909399;
  font-weight: 600;
  margin-bottom: 4px;
}

.merma-desglose-item {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  color: #e6a23c;
  padding: 2px 0;
}

.merma-desglose-nombre {
  color: #303133;
  font-weight: 700;
  min-width: 110px;
}

.relative-container {
  position: relative;
}
.blur-content {
  filter: blur(3px);
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
  transition: all 0.3s ease;
}
.stale-overlay {
  position: absolute;
  top: 20%;
  left: 10%;
  right: 10%;
  background: rgba(255, 255, 255, 0.95);
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e6a23c;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;
}
.stale-icon-large {
  font-size: 32px;
  color: #e6a23c;
  margin-bottom: 8px;
}
.stale-overlay span {
  font-size: 13px;
  font-weight: 700;
  color: #303133;
}
.stale-overlay .stale-sub {
  font-size: 11px;
  font-weight: 400;
  color: #606266;
  margin-top: 4px;
}

/* Varios */
.empty-state {
  text-align: center;
  padding: 20px;
  font-size: 12px;
  color: #909399;
}
.floating-toggle-btn {
  position: fixed;
  bottom: 44px;
  right: 32px;
  z-index: 1999;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #409eff;
  color: white;
  border: none;
  cursor: pointer;
  font-size: 22px;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.data-group {
  margin-bottom: 12px;
  background: #f8f9fa;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #ebeef5;
}
.group-title {
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 800;
  color: #909399;
  margin-bottom: 6px;
  letter-spacing: 0.5px;
}
</style>
